// 004 (contracts/consumes.md; research R7, R8): thin wrappers over the generated client for the
// /me* operations. Each throws one AppError on failure (`unwrap`). Screens read them through
// ProfileClientContext so tests can inject fakes. Requests send a school only as its code (BR-14).
import { createContext, useContext } from "react";

import { api } from "@/api";
import { type ApiClient, unwrap } from "@/api/client";
import type { Api } from "@/api/types";
import { listMyStudents } from "@/join/joinApi";

export type ProfileClient = {
  getProfile(): Promise<Api["Profile"]>;
  updateProfile(body: Api["ProfileUpdate"]): Promise<Api["Profile"]>;
  listStudents(): Promise<Api["StudentList"]>;
  getStudent(id: string): Promise<Api["Student"]>;
  addStudent(body: Api["StudentCreate"], key: string): Promise<Api["Student"]>;
  updateStudent(
    id: string,
    body: Api["StudentUpdate"],
  ): Promise<Api["Student"]>;
  removeStudent(id: string): Promise<void>;
  listAddresses(): Promise<Api["AddressList"]>;
  getAddress(id: string): Promise<Api["Address"]>;
  addAddress(body: Api["AddressCreate"], key: string): Promise<Api["Address"]>;
  updateAddress(
    id: string,
    body: Api["AddressUpdate"],
  ): Promise<Api["Address"]>;
  deleteAddress(id: string): Promise<void>;
  setDefaultAddress(id: string): Promise<Api["Address"]>;
};

const sid = (studentId: string) => ({ params: { path: { studentId } } });
const aid = (addressId: string) => ({ params: { path: { addressId } } });

export function createProfileClient(client: ApiClient): ProfileClient {
  return {
    getProfile: () => unwrap(client.GET("/api/v1/me")),
    updateProfile: (body) => unwrap(client.PATCH("/api/v1/me", { body })),
    listStudents: () => listMyStudents(client),
    getStudent: (id) =>
      unwrap(client.GET("/api/v1/me/students/{studentId}", sid(id))),
    addStudent: (body, key) =>
      unwrap(
        client.POST("/api/v1/me/students", {
          body,
          params: { header: { "Idempotency-Key": key } },
        }),
      ),
    updateStudent: (id, body) =>
      unwrap(
        client.PATCH("/api/v1/me/students/{studentId}", { ...sid(id), body }),
      ),
    removeStudent: async (id) => {
      await unwrap(client.DELETE("/api/v1/me/students/{studentId}", sid(id)));
    },
    listAddresses: () => unwrap(client.GET("/api/v1/me/addresses")),
    getAddress: (id) =>
      unwrap(client.GET("/api/v1/me/addresses/{addressId}", aid(id))),
    addAddress: (body, key) =>
      unwrap(
        client.POST("/api/v1/me/addresses", {
          body,
          params: { header: { "Idempotency-Key": key } },
        }),
      ),
    updateAddress: (id, body) =>
      unwrap(
        client.PATCH("/api/v1/me/addresses/{addressId}", { ...aid(id), body }),
      ),
    deleteAddress: async (id) => {
      await unwrap(client.DELETE("/api/v1/me/addresses/{addressId}", aid(id)));
    },
    setDefaultAddress: (id) =>
      unwrap(client.POST("/api/v1/me/addresses/{addressId}/default", aid(id))),
  };
}

export const appProfileClient: ProfileClient = createProfileClient(api);

export const ProfileClientContext =
  createContext<ProfileClient>(appProfileClient);
export const useProfileClient = () => useContext(ProfileClientContext);
